import { apiGet, apiPost, apiPut, apiDelete, apiPostFormData, getAccessToken } from './api';
import API_CONFIG from '../config/api';

/* =========================
   EVENTOS (ADMIN)
========================= */

export const getEventosAdmin = async () => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.ADMIN_LISTAR);
  const data = await response.json();

  if (!Array.isArray(data)) {
    throw new Error('Respuesta inválida al listar eventos');
  }

  return data;
};

export const getEventoById = async (id) => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.OBTENER(id));
  return response.json();
};

export const crearEvento = async (payload) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.EVENTOS.CREAR, payload);
  const data = await response.json();

  if (!data.ok) {
    throw new Error(data.error || 'Error al crear evento');
  }

  return data;
};

export const editarEvento = async (id, payload) => {
  const response = await apiPut(
    API_CONFIG.ENDPOINTS.EVENTOS.EDITAR(id),
    payload
  );

  const data = await response.json();
  if (!data.ok) {
    throw new Error(data.error || 'Error al actualizar evento');
  }

  return data;
};

export const cerrarEvento = async (id) => {
  const response = await apiPost(
    API_CONFIG.ENDPOINTS.EVENTOS.CERRAR(id)
  );

  const data = await response.json();
  if (!data.ok) {
    throw new Error('No se pudo cerrar el evento');
  }

  return data;
};

export const postularEvento = async (payload) => {
  const response = await apiPost(
    API_CONFIG.ENDPOINTS.EVENTOS.POSTULAR,
    payload
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Error al postular evento');
  }
  return data;
};

export const getPostulaciones = async () => {
  const response = await apiGet(
    API_CONFIG.ENDPOINTS.EVENTOS.POSTULACIONES
  );
  return response.json();
};

export const getPostulacionesHistorial = async () => {
  const response = await apiGet(
    API_CONFIG.ENDPOINTS.EVENTOS.POSTULACIONES_HISTORIAL
  );
  return response.json();
};

export const aprobarEvento = async (id) => {
  const response = await apiPost(
    API_CONFIG.ENDPOINTS.EVENTOS.APROBAR(id)
  );

  const data = await response.json();
  if (!data.ok) {
    throw new Error(data.error || 'Error al aprobar evento');
  }

  return data;
};

export const rechazarEvento = async (id) => {
  let response;
  try {
    response = await apiPost(
      API_CONFIG.ENDPOINTS.EVENTOS.RECHAZAR(id)
    );
  } catch (err) {
    if (err.message && (err.message.includes('no existe') || err.message.includes('404'))) {
      throw new Error('No se pudo rechazar. Despliega la última versión del BFF (debe incluir el endpoint POST /api/eventos/.../rechazar).');
    }
    throw err;
  }

  const data = await response.json();
  if (!data.ok) {
    throw new Error(data.error || 'Error al rechazar postulación');
  }

  return data;
};

/* =========================
   INSCRITOS (ADMIN)
========================= */
export const getInscritos = async (eventoId) => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.INSCRITOS(eventoId));
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Error al listar inscritos');
  }
  return Array.isArray(data) ? data : [];
};

/** Descarga CSV de inscritos (requiere JWT admin; no usar window.open). */
export const downloadInscritosCsv = async (eventoId) => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EVENTOS.INSCRITOS_CSV(eventoId)}`;
  const token = getAccessToken();
  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
    cache: 'no-cache',
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = 'No se pudo descargar el CSV';
    try {
      const j = JSON.parse(text);
      msg = j.message || j.error || msg;
    } catch (_) {
      if (text) msg = text.slice(0, 200);
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `inscritos_evento_${eventoId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

/**
 * Descarga las categorías del torneo en el template de divisiones de LiftingCast.
 * Devuelve el resumen { base, overall, omitidos } que envía el backend.
 */
export const downloadCategoriasCsv = async (eventoId) => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EVENTOS.CATEGORIAS_CSV(eventoId)}`;
  const token = getAccessToken();
  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
    cache: 'no-cache',
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = 'No se pudieron descargar las categorías';
    try {
      const j = JSON.parse(text);
      msg = j.message || j.error || msg;
    } catch (_) {
      if (text) msg = text.slice(0, 200);
    }
    throw new Error(msg);
  }
  const resumen = {
    base: Number(res.headers.get('X-Categorias-Base') ?? NaN),
    overall: Number(res.headers.get('X-Categorias-Overall') ?? NaN),
    omitidos: Number(res.headers.get('X-Inscritos-Omitidos') ?? 0),
  };
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `categorias_evento_${eventoId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return resumen;
};

/**
 * Descarga los inscritos en el template de importación de atletas de LiftingCast.
 * Devuelve { atletas, omitidos } que envía el backend.
 */
export const downloadInscritosLiftingCastCsv = async (eventoId) => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EVENTOS.INSCRITOS_LIFTINGCAST_CSV(eventoId)}`;
  const token = getAccessToken();
  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
    cache: 'no-cache',
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = 'No se pudieron descargar los inscritos';
    try {
      const j = JSON.parse(text);
      msg = j.message || j.error || msg;
    } catch (_) {
      if (text) msg = text.slice(0, 200);
    }
    throw new Error(msg);
  }
  const resumen = {
    atletas: Number(res.headers.get('X-Atletas') ?? NaN),
    omitidos: Number(res.headers.get('X-Inscritos-Omitidos') ?? 0),
  };
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `inscritos_liftingcast_evento_${eventoId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return resumen;
};

export const addInscrito = async (eventoId, payload) => {
  const response = await apiPost(
    API_CONFIG.ENDPOINTS.EVENTOS.INSCRITOS(eventoId),
    payload
  );
  const data = await response.json();
  if (!data.ok) {
    throw new Error(data.error || 'Error al agregar inscrito');
  }
  return data;
};

export const uploadInscritosExcel = async (eventoId, file) => {
  const response = await apiPostFormData(
    API_CONFIG.ENDPOINTS.EVENTOS.INSCRITOS_UPLOAD(eventoId),
    file,
    'file'
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Error al subir Excel');
  }
  return data;
};

export const updateInscrito = async (eventoId, inscripcionId, payload) => {
  const response = await apiPut(
    API_CONFIG.ENDPOINTS.EVENTOS.INSCRITO_DELETE(eventoId, inscripcionId),
    payload
  );
  const data = await response.json();
  if (!data.ok) {
    throw new Error(data.error || 'Error al actualizar inscrito');
  }
  return data;
};

export const deleteInscrito = async (eventoId, inscripcionId) => {
  const response = await apiDelete(
    API_CONFIG.ENDPOINTS.EVENTOS.INSCRITO_DELETE(eventoId, inscripcionId)
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Error al eliminar inscrito');
  }
  return data;
};

/* Lista maestra de participantes (pool). Opcional: { desde, hasta } en YYYY-MM-DD para filtrar por fecha de alta. */
export const getParticipantesPool = async (filtros = {}) => {
  const params = new URLSearchParams();
  if (filtros.desde) params.set('desde', filtros.desde);
  if (filtros.hasta) params.set('hasta', filtros.hasta);
  if (filtros.origen) params.set('origen', filtros.origen);
  const qs = params.toString();
  const url = qs ? `${API_CONFIG.ENDPOINTS.EVENTOS.PARTICIPANTES_POOL}?${qs}` : API_CONFIG.ENDPOINTS.EVENTOS.PARTICIPANTES_POOL;
  const response = await apiGet(url);
  if (!response.ok) throw new Error('Error al listar participantes');
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const getOrigenesPool = async () => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.PARTICIPANTES_POOL_ORIGENES);
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const uploadParticipantesPoolExcel = async (file, tituloCarga = '') => {
  const response = await apiPostFormData(
    API_CONFIG.ENDPOINTS.EVENTOS.PARTICIPANTES_POOL_UPLOAD,
    file,
    'file',
    tituloCarga ? { titulo_carga: tituloCarga } : null
  );
  const data = await response.json();
  if (!response.ok) {
    const msg = data.error || data.message || 'Error al subir Excel';
    throw new Error(msg);
  }
  return data;
};

export const getParticipantesDisponibles = async (eventoId) => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.PARTICIPANTES_DISPONIBLES(eventoId));
  if (!response.ok) throw new Error('Error al listar disponibles');
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const addInscritosDesdePool = async (eventoId, ruts) => {
  const response = await apiPost(
    API_CONFIG.ENDPOINTS.EVENTOS.INSCRITOS_DESDE_POOL(eventoId),
    { ruts }
  );
  const data = await response.json();
  if (!data.ok) throw new Error(data.error || 'Error al agregar desde lista');
  return data;
};


/** Inscripciones del usuario autenticado (para "Mis eventos" del perfil). */
export const getMisInscripciones = async () => {
  const res = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.MIS_INSCRIPCIONES);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || 'No se pudieron cargar tus eventos');
  return data.items || [];
};

/** Inscripción vigente del usuario en un evento, o null si no está inscrito. */
export const getMiInscripcion = async (eventoId) => {
  const res = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.MI_INSCRIPCION(eventoId));
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || 'No se pudo revisar tu inscripción');
  return data.inscripcion || null;
};
